import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Container, Divider, Fab, Grid, Typography } from '@mui/material';
import { FieldFormik, Fieldrow, FileInput, TextEditor } from 'components/atoms';
import { AdminLayout } from 'components/organisms';
import { createSchema } from 'constants/form/how-to-use-toolkit-form';
import { Field, FieldArray, Form, Formik, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { useGetToolkitData, useUpdateToolkitData } from 'modules/toolkit';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

type PhraseProp = {
	content: string;
	color: string;
	scale: number;
	z_index?: number;
};

const AdminHowToUseToolkitCMS: React.FC = () => {
	const saveButtonRef = React.useRef<HTMLButtonElement>(null);
	const [showScrollUp, setShowScrollUp] = React.useState(false);
	const [fileInputError, setFileInputError] = React.useState<string | null>(null);

	React.useEffect(() => {
		const handleScroll = () => {
			if (!saveButtonRef.current) return;
			const rect = saveButtonRef.current.getBoundingClientRect();
			// If save button is above the viewport, show the arrow
			setShowScrollUp(rect.top < 0);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const scrollToSaveButton = () => {
		if (saveButtonRef.current) {
			saveButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};
	const { data } = useGetToolkitData();

	const { mutate, isLoading } = useUpdateToolkitData();

	return (
		<AdminLayout>
			<section>
				<Formik
					enableReinitialize
					initialValues={{
						hero_banner_toolkit: data?.hero_banner_toolkit || {
							headline: '',
							image: {
								file: null,
							},
						},
						how_to_use_section_toolkit: data?.how_to_use_section_toolkit || {
							name: '',
							headline: '',
							body: '',
							button: '',
							steps: [],
						},
						objectives_section_toolkit: data?.objectives_section_toolkit || {
							name: '',
							headline: '',
							objectives: [],
						},
						where_does_the_toolkit_fit_section_toolkit: data?.where_does_the_toolkit_fit_section_toolkit || {
							name: '',
							headline: '',
							body: '',
							phrases: [],
						},
					}}
					validationSchema={createSchema}
					validateOnMount
					onSubmit={() => {}}
				>
					{({ values, errors, dirty, setFieldValue }: FormikProps<any>) => {
						const postData = async () => {
							const data = new FormData();

							data.append('hero_banner_toolkit[headline]', values.hero_banner_toolkit.headline);
							data.append(
								'hero_banner_toolkit[image]',
								values.hero_banner_toolkit.image?.file || values.hero_banner_toolkit.image
							);
							data.append('how_to_use_section_toolkit[name]', values.how_to_use_section_toolkit.name);
							data.append('how_to_use_section_toolkit[headline]', values.how_to_use_section_toolkit.headline);

							data.append('how_to_use_section_toolkit[body]', values.how_to_use_section_toolkit.body);
							data.append('how_to_use_section_toolkit[button]', values.how_to_use_section_toolkit.button);

							values.how_to_use_section_toolkit.steps.forEach((step: any, index: number) => {
								data.append(`how_to_use_section_toolkit[steps][${index}][title]`, step.title);
								data.append(`how_to_use_section_toolkit[steps][${index}][description]`, step.description);
								data.append(`how_to_use_section_toolkit[steps][${index}][image]`, step.image?.file || step.image);
							});
							data.append('objectives_section_toolkit[name]', values.objectives_section_toolkit.name);
							data.append('objectives_section_toolkit[headline]', values.objectives_section_toolkit.headline);
							values.objectives_section_toolkit.objectives.forEach((objective: any, index: number) => {
								data.append(`objectives_section_toolkit[objectives][${index}][title]`, objective.title);
								data.append(`objectives_section_toolkit[objectives][${index}][description]`, objective.description);
								data.append(
									`objectives_section_toolkit[objectives][${index}][image]`,
									objective.image?.file || objective.image
								);
							});

							data.append(
								'where_does_the_toolkit_fit_section_toolkit[name]',
								values.where_does_the_toolkit_fit_section_toolkit.name
							);
							data.append(
								'where_does_the_toolkit_fit_section_toolkit[headline]',
								values.where_does_the_toolkit_fit_section_toolkit.headline
							);
							data.append(
								'where_does_the_toolkit_fit_section_toolkit[body]',
								values.where_does_the_toolkit_fit_section_toolkit.body
							);
							values.where_does_the_toolkit_fit_section_toolkit.phrases.forEach((phrase: PhraseProp, index: number) => {
								data.append(`where_does_the_toolkit_fit_section_toolkit[phrases][${index}][content]`, phrase.content);
								data.append(`where_does_the_toolkit_fit_section_toolkit[phrases][${index}][color]`, phrase.color);
								data.append(
									`where_does_the_toolkit_fit_section_toolkit[phrases][${index}][scale]`,
									phrase.scale.toString()
								);
								data.append(
									`where_does_the_toolkit_fit_section_toolkit[phrases][${index}][z_index]`,
									phrase.z_index?.toString() || '0'
								);
							});

							await mutate(data, {
								onSuccess: () => {
									toast.success(`How to use ToolKit CMS page has been updated successfully`);
								},
								onError: e => {
									const errorType = e.response?.data?.error?.errors;
									if (Array.isArray(errorType) && errorType.length > 0) {
										errorType.forEach((element: any) => {
											toast.error(element?.message || 'An unexpected error occurred');
										});
									} else {
										toast.error(e.response?.data?.error?.message || 'An unexpected error occurred');
									}
								},
							});
						};

						return (
							<Form>
								<Container
									maxWidth="xl"
									sx={{
										'& a': { textDecoration: 'none' },
									}}
								>
									<Box display="flex" alignItems="center" width="100%" flexWrap="wrap" gap={2}>
										<Box flex={1} flexShrink={0}>
											<Typography fontSize="32px" fontWeight="600" whiteSpace="nowrap">
												How to use Toolkit CMS
											</Typography>
											<Typography variant="body2" mt={0.5} sx={{ color: '#475467' }} whiteSpace="nowrap">
												Manage and edit How to use Toolkit content
											</Typography>
										</Box>
										<Box display="flex">
											<Link to="/admin/users">
												<Button
													variant="outlined"
													sx={{
														textDecoration: 'none!important',
														background: 'white',
														color: 'black',
														border: '1px solid #D0D5DD',
													}}
												>
													Cancel
												</Button>
											</Link>
											<Button
												disabled={!isEmpty(errors) || isLoading || !dirty || !!fileInputError}
												onClick={() => postData()}
												sx={{ ml: 3 }}
												variant="contained"
												ref={saveButtonRef}
											>
												Save
											</Button>
										</Box>
									</Box>
								</Container>
								<Divider sx={{ my: 3 }} />

								<Container maxWidth="xl">
									<Grid container justifyContent="space-between">
										<Grid md={3} xs={12} item>
											<Typography sx={{ mb: 2 }} fontWeight="600">
												Herobanner
											</Typography>
										</Grid>
										<Grid md={8} xs={12} item>
											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Headline
												</Typography>
												<Field name="hero_banner_toolkit.headline">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.hero_banner_toolkit?.headline}
															onchange={field.onChange(field.name)}
															maxLength={100}
														/>
													)}
												</Field>
											</Container>

											<Container sx={{ mb: 4 }}>
												<Typography fontWeight="600">Image</Typography>
												<Typography sx={{ mb: 2 }}>
													Please resize the picture to exactly 1080x1080px before uploading it
												</Typography>
												<Fieldrow name="hero_banner_toolkit.image.file">
													<FileInput
														previewImg={values?.hero_banner_toolkit?.image_url}
														name="hero_banner_toolkit.image.file"
														label=""
														maxsize="SVG, PNG, JPG or GIF (max. 1080x1080px)"
														maxHeight={1080}
														maxWidth={1080}
														onErrorChange={setFileInputError}
													/>
												</Fieldrow>
											</Container>
										</Grid>
									</Grid>
								</Container>
								<Divider sx={{ my: 3 }} />

								<Container maxWidth="xl">
									<Grid container justifyContent="space-between">
										<Grid md={3} xs={12} item>
											<Typography sx={{ mb: 2 }} fontWeight="600">
												Section
											</Typography>
										</Grid>
										<Grid md={8} xs={12} item>
											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Tag
												</Typography>
												<Fieldrow name="how_to_use_section_toolkit.name">
													<FieldFormik type="text" name="how_to_use_section_toolkit.name" maxLength={50} />
												</Fieldrow>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Headline
												</Typography>
												<Field name="how_to_use_section_toolkit.headline">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.how_to_use_section_toolkit?.headline}
															onchange={field.onChange(field.name)}
															maxLength={100}
														/>
													)}
												</Field>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Body
												</Typography>
												<Field name="how_to_use_section_toolkit.body">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.how_to_use_section_toolkit?.body}
															onchange={field.onChange(field.name)}
															maxLength={800}
														/>
													)}
												</Field>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Button
												</Typography>
												<Fieldrow name="how_to_use_section_toolkit.button">
													<FieldFormik type="text" name="how_to_use_section_toolkit.button" maxLength={50} />
												</Fieldrow>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Steps
												</Typography>
												<FieldArray name="how_to_use_section_toolkit.steps">
													{({ push, remove }) => (
														<>
															{values.how_to_use_section_toolkit.steps?.map((step: any, index: number) => (
																<Box key={index} sx={{ mt: 2, mb: 2, border: '1px solid #ddd', p: 2, borderRadius: 2 }}>
																	<Box>
																		<Typography sx={{ mb: 2 }} fontWeight="600">
																			Title
																		</Typography>
																		<FieldFormik
																			type="text"
																			name={`how_to_use_section_toolkit.steps[${index}].title`}
																			maxLength={100}
																		/>
																	</Box>
																	<Box>
																		<Typography sx={{ mt: 2, mb: 2 }} fontWeight="600">
																			Description
																		</Typography>
																		<Field name={`how_to_use_section_toolkit.steps[${index}].description`}>
																			{({ field, meta }: any) => (
																				<TextEditor
																					value={values.how_to_use_section_toolkit?.steps?.[index]?.description}
																					onchange={field.onChange(field.name)}
																					maxLength={800}
																				/>
																			)}
																		</Field>
																	</Box>
																	<Box>
																		<Typography sx={{ mt: 2 }} fontWeight="600">
																			Image
																		</Typography>
																		<Typography sx={{ mt: 2, mb: 2 }}>
																			Upload an image (SVG, PNG, JPG or GIF)
																		</Typography>
																		<Fieldrow name={`how_to_use_section_toolkit.steps[${index}].image.file`}>
																			<FileInput
																				previewImg={values?.how_to_use_section_toolkit?.steps?.[index]?.image_url}
																				name={`how_to_use_section_toolkit.steps[${index}].image.file`}
																				label=""
																				maxsize="SVG, PNG, JPG or GIF (max. 1080x1080px)"
																				maxHeight={1080}
																				maxWidth={1080}
																				onErrorChange={setFileInputError}
																			/>
																		</Fieldrow>
																	</Box>
																	<Button color="error" onClick={() => remove(index)} sx={{ mt: 1 }}>
																		Remove
																	</Button>
																</Box>
															))}
															<Button
																variant="outlined"
																onClick={() => push({ title: '', description: '', image: '' })}
															>
																+ Add
															</Button>
														</>
													)}
												</FieldArray>
											</Container>
										</Grid>
									</Grid>
								</Container>
								<Divider sx={{ my: 3 }} />

								<Container maxWidth="xl">
									<Grid container justifyContent="space-between">
										<Grid md={3} xs={12} item>
											<Typography sx={{ mb: 2 }} fontWeight="600">
												Section
											</Typography>
										</Grid>
										<Grid md={8} xs={12} item>
											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Tag
												</Typography>
												<Fieldrow name="objectives_section_toolkit.name">
													<FieldFormik type="text" name="objectives_section_toolkit.name" maxLength={50} />
												</Fieldrow>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Headline
												</Typography>
												<Field name="objectives_section_toolkit.headline">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.objectives_section_toolkit?.headline}
															onchange={field.onChange(field.name)}
															maxLength={800}
														/>
													)}
												</Field>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Objectives
												</Typography>
												<FieldArray name="objectives_section_toolkit.objectives">
													{({ push, remove }) => (
														<>
															{values.objectives_section_toolkit.objectives?.map((objective: any, index: number) => (
																<Box key={index} sx={{ mb: 2, border: '1px solid #ddd', p: 2, borderRadius: 2 }}>
																	<Box>
																		<Typography sx={{ mb: 2 }} fontWeight="600">
																			Title
																		</Typography>
																		<FieldFormik
																			type="text"
																			name={`objectives_section_toolkit.objectives[${index}].title`}
																			maxLength={100}
																		/>
																	</Box>
																	<Box>
																		<Typography sx={{ mt: 2, mb: 2 }} fontWeight="600">
																			Description
																		</Typography>
																		<Field name={`objectives_section_toolkit.objectives[${index}].description`}>
																			{({ field, meta }: any) => (
																				<TextEditor
																					value={values.objectives_section_toolkit?.objectives?.[index]?.description}
																					onchange={field.onChange(field.name)}
																					maxLength={800}
																				/>
																			)}
																		</Field>
																	</Box>
																	<Box sx={{ mt: 2 }}>
																		<Typography sx={{ mt: 2, mb: 2 }} fontWeight="600">
																			Icon
																		</Typography>
																		<Typography sx={{ mt: 2, mb: 2 }}>Upload an icon (SVG, PNG, JPG or GIF)</Typography>
																		<Fieldrow name={`objectives_section_toolkit.objectives[${index}].image.file`}>
																			<FileInput
																				previewImg={values?.objectives_section_toolkit?.objectives?.[index]?.image_url}
																				name={`objectives_section_toolkit.objectives[${index}].image.file`}
																				label=""
																				maxsize="SVG, PNG, JPG or GIF (max. 1080x1080px)"
																				maxHeight={1080}
																				maxWidth={1080}
																				onErrorChange={setFileInputError}
																			/>
																		</Fieldrow>
																	</Box>
																	<Button color="error" onClick={() => remove(index)} sx={{ mt: 1 }}>
																		Remove
																	</Button>
																</Box>
															))}
															<Button
																variant="outlined"
																onClick={() => push({ title: '', description: '', image: '' })}
															>
																+ Add
															</Button>
														</>
													)}
												</FieldArray>
											</Container>
										</Grid>
									</Grid>
								</Container>
								<Divider sx={{ my: 3 }} />

								<Container maxWidth="xl">
									<Grid container justifyContent="space-between">
										<Grid md={3} xs={12} item>
											<Typography sx={{ mb: 2 }} fontWeight="600">
												Section
											</Typography>
										</Grid>
										<Grid md={8} xs={12} item>
											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Tag
												</Typography>
												<Fieldrow name="where_does_the_toolkit_fit_section_toolkit.name">
													<FieldFormik
														type="text"
														name="where_does_the_toolkit_fit_section_toolkit.name"
														maxLength={50}
													/>
												</Fieldrow>
											</Container>
											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Headline
												</Typography>
												<Field name="where_does_the_toolkit_fit_section_toolkit.headline">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.where_does_the_toolkit_fit_section_toolkit?.headline}
															onchange={field.onChange(field.name)}
															maxLength={50}
														/>
													)}
												</Field>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Body
												</Typography>
												<Field name="where_does_the_toolkit_fit_section_toolkit.body">
													{({ field, meta }: any) => (
														<TextEditor
															value={values.where_does_the_toolkit_fit_section_toolkit?.body}
															onchange={field.onChange(field.name)}
															maxLength={800}
														/>
													)}
												</Field>
											</Container>

											<Container maxWidth="md" sx={{ mb: 4 }}>
												<Typography sx={{ mb: 2 }} fontWeight="600">
													Phrases
												</Typography>
												<FieldArray name="where_does_the_toolkit_fit_section_toolkit.phrases">
													{({ push, remove }) => (
														<>
															{values.where_does_the_toolkit_fit_section_toolkit.phrases?.map(
																(phrase: PhraseProp, index: number) => (
																	<Box key={index} sx={{ mb: 2, border: '1px solid #ddd', p: 2, borderRadius: 2 }}>
																		<Box>
																			<Typography sx={{ mb: 2 }} fontWeight="600">
																				Content
																			</Typography>
																			<FieldFormik
																				type="text"
																				name={`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].content`}
																				maxLength={70}
																			/>
																		</Box>
																		<Box sx={{ mt: 2 }}>
																			<Typography sx={{ mb: 1 }} fontWeight="600">
																				Color
																			</Typography>
																			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
																				<Box sx={{ flex: '0 0 80%' }}>
																					<FieldFormik
																						type="color"
																						name={`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].color`}
																						label=""
																					/>
																				</Box>
																				<Box sx={{ flex: '0 0 20%', display: 'flex', alignItems: 'center', gap: 1 }}>
																					<Box
																						sx={{
																							width: 30,
																							height: 30,
																							backgroundColor: phrase.color,
																							border: '1px solid #ccc',
																							borderRadius: 1,
																							flexShrink: 0,
																						}}
																					/>
																					<Typography
																						variant="caption"
																						sx={{
																							fontFamily: 'monospace',
																							fontSize: '12px',
																							wordBreak: 'break-all',
																						}}
																					>
																						{phrase.color}
																					</Typography>
																				</Box>
																			</Box>
																		</Box>
																		<Box sx={{ mt: 2 }}>
																			<FieldFormik
																				type="number"
																				min={1}
																				max={1.3}
																				step={0.05}
																				name={`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].scale`}
																				label="Scale (1 - 1.3)"
																				setFieldValue={setFieldValue}
																				onBlur={e => {
																					let val = Number(e.target.value);
																					if (val < 1) val = 1;
																					if (val > 1.3) val = 1.3;
																					setFieldValue(
																						`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].scale`,
																						val
																					);
																				}}
																			/>
																		</Box>
																		<Box sx={{ mt: 2 }}>
																			<FieldFormik
																				type="number"
																				name={`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].z_index`}
																				label="Overlap priority (0 - 100)"
																				min={0}
																				max={100}
																				step={1}
																				setFieldValue={setFieldValue}
																				onBlur={e => {
																					let val = Number(e.target.value);
																					if (val < 0) val = 0;
																					if (val > 100) val = 100;
																					setFieldValue(
																						`where_does_the_toolkit_fit_section_toolkit.phrases[${index}].z_index`,
																						val
																					);
																				}}
																			/>
																		</Box>
																		<Button color="error" onClick={() => remove(index)} sx={{ mt: 1 }}>
																			Remove
																		</Button>
																	</Box>
																)
															)}
															<Button
																variant="outlined"
																onClick={() => push({ content: '', color: '#000000', scale: 1, z_index: 0 })}
															>
																+ Add
															</Button>
														</>
													)}
												</FieldArray>
											</Container>
										</Grid>
									</Grid>
								</Container>
								{/* Floating Up Arrow Button */}
								{showScrollUp && (
									<Fab
										color="primary"
										aria-label="scroll up"
										onClick={scrollToSaveButton}
										sx={{
											position: 'fixed',
											bottom: 32,
											right: 32,
											zIndex: 9999,
											width: 35,
											height: 35,
											minHeight: 0,
											borderRadius: '50%',
											boxShadow: 3,
										}}
									>
										<KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
									</Fab>
								)}
							</Form>
						);
					}}
				</Formik>
			</section>
		</AdminLayout>
	);
};
export default AdminHowToUseToolkitCMS;
